import * as React from "react";
import * as _ from "lodash";
import { useDispatch } from "redux-react-hook";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import { Switch } from "src/library/components/Forms";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { CascadeSettingFieldLabel } from "src/library/components/Forms/CascadeSettingField";
import { Text } from "src/library/components/Text";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  OrderedInheritedSettings,
  IInheritedSetting,
  InheritedSettingValue,
  CreditRequestAutoApprovalSetting
} from "src/core/drops/api/drops.api";
import {
  InheritedRadioRow,
  CreditRequestAutoApprovalThresholds,
  InheritedSettingsWrapper,
  InheritedSettingsField
} from "src/core/shared/components/Inherited-settings";
import { useCreditReasonCodes } from "src/modules/credit-request/hooks/useCreditReasonCodes";
import {
  CreateSettingParams,
  useInheritedSettingsApi
} from "src/core/shared/api/inheritedSettings.api";
import { useDrop } from "src/core/drops/hooks";

interface DropCreditRequestSettingsPageProps {
  customerId: string;
  dropId: string;
}

type IGlobalSettingFormFields = { [P in IFormFields]: IFormValue<IFormFields> };
type IFormFields = keyof Pick<InheritedSettingValue, "credit_request_auto_approval">;

interface IFormValue<T> {
  name: T;
  label: string | number;
  inherited: boolean;
  inheritedValue?: any;
  value?: any;
  Description?: JSX.Element;
  SettingElement: (setting: IFormValue<T>) => React.ReactNode;
  hidden?: boolean;
}

type IFormValues = { [P in IFormFields]?: InheritedSettingValue[IFormFields] };

/**
 * Drop credit request settings page
 */
const DropCreditRequestSettingsPage: React.ReactFCWithChildren<PageProps<unknown, DropCreditRequestSettingsPageProps>> = props => {
  const { match, terminology } = props;
  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const dropId = match ? parseInt(match.params.dropId, 0) : 0;
  const [ { isLoading: isLoadingDrop, drop } ] = useDrop(dropId);
  const checkPermission = useCheckUserPermission();
  const dispatch = useDispatch();
  const creditRequestReasons = useCreditReasonCodes();
  const [ getInheritedSettingsRes, getInheritedSettingsReq ] = useInheritedSettingsApi("GET_BRANCH_SETTINGS");
  const [ createSettingRes, createSettingReq ] = useInheritedSettingsApi("CREATE_SETTING");
  const [ updateSettingRes, updateSettingReq ] = useInheritedSettingsApi("UPDATE_SETTING");
  const [ inheritedSettings, setInheritedSettings ] = React.useState<OrderedInheritedSettings<IInheritedSetting>>();
  const [ overrideSettings, setOverrideSettings ] = React.useState<OrderedInheritedSettings<IInheritedSetting>>();
  const canAmend = React.useMemo(() => checkPermission("amend-global-settings"), []);
  const [ formValues, setFormValues ] = React.useState<IFormValues>();
  const isLoading = React.useMemo(() => createSettingRes.pending || updateSettingRes.pending, [ createSettingRes.pending, updateSettingRes.pending ]);
  const { handleSubmit } = useForm<IGlobalSettingFormFields>();

  /**
   * Compile the settings
   */
  const settings: IGlobalSettingFormFields | null = React.useMemo(() => {
    const creditRequestInheritedSetting: InheritedSettingValue["credit_request_auto_approval"] | null = (inheritedSettings?.credit_request_auto_approval?.value as InheritedSettingValue["credit_request_auto_approval"]) || null;

    // Return null if there are no settings found
    if (!inheritedSettings) {
      return null;
    }

    return {
      credit_request_auto_approval: {
        name: "credit_request_auto_approval",
        // label: creditRequestInheritedSetting?.enabled ? `Auto-approve (${creditRequestInheritedSetting.threshold ? `Flat: ${creditRequestInheritedSetting.threshold}%` : `Reason specific - (${creditRequestInheritedSetting.thresholds?.map(threshold => `${threshold.reasonCode}: ${threshold.autoApprovalThreshold}%)`).join(" ")` })` : "Manual approval required",
        label: creditRequestInheritedSetting?.enabled ?
          `Auto-approve (${
            typeof creditRequestInheritedSetting.threshold !== "undefined" ?
              `Flat: ${creditRequestInheritedSetting.threshold}%)` :
              creditRequestInheritedSetting.thresholds ?
                `Reason specific - ${creditRequestInheritedSetting.thresholds.map(threshold => `${(threshold.reasonCode.replace("_", " "))}: ${threshold.autoApprovalThreshold}%`).join(", ")})` :
                ""}` :
          "Manual approval required",
        inherited: true,
        inheritedValue: creditRequestInheritedSetting ? creditRequestInheritedSetting : { enabled: false },
        value: overrideSettings && overrideSettings.credit_request_auto_approval?.value ? overrideSettings.credit_request_auto_approval.value : { enabled: false },
        Description: (
          <>
            <Text
              as="h2"
              fontSize="h3"
            >
              Credit request auto-approval
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This sets whether a ${terminology.customer.singular.text} credit requests can be automatically approved based on the given criteria.`}
            </Text>
          </>
        ),
        SettingElement: setting => (
          <>
            <Text
              as="p"
              className="default-value"
              weight="bold"
            >
              {`Default: ${setting.label}`}
            </Text>

            <InheritedRadioRow direction="horizontal">
              <Switch
                disabled={!canAmend}
                onChange={checked => {
                  // Update this field value
                  setFormValues(current => {
                    const parsedCurrent = current || {};
                    const overrideCreditRequestThreshold = overrideSettings?.credit_request_auto_approval?.value as CreditRequestAutoApprovalSetting | undefined;

                    // If there is no threshold(s) data in the form, set a default.
                    // Either from the override setting or a hardcoded default it a
                    // override setting does not exist
                    if (!(current?.credit_request_auto_approval?.threshold || current?.credit_request_auto_approval?.thresholds)) {
                      // If there is override
                      if (overrideCreditRequestThreshold) {
                        return {
                          ...parsedCurrent,
                          credit_request_auto_approval: {
                            ...overrideCreditRequestThreshold,
                            enabled: checked
                          }
                        };
                      }

                      return {
                        ...parsedCurrent,
                        credit_request_auto_approval: {
                          enabled: checked,
                          threshold: 0
                        }
                      };
                    }

                    return {
                      ...parsedCurrent,
                      credit_request_auto_approval: {
                        ...parsedCurrent[ "credit_request_auto_approval" ],
                        enabled: checked
                      }
                    };
                  });
                }}
                checked={formValues?.credit_request_auto_approval?.enabled || false}
              />

              <CascadeSettingFieldLabel
                for={`${setting.name}-default`}
                label={`Auto-approve ${terminology.customer.singular.text} credit requests`}
              />
            </InheritedRadioRow>

            <CreditRequestAutoApprovalThresholds
              data={formValues?.credit_request_auto_approval}
              overrideData={overrideSettings?.credit_request_auto_approval?.value as CreditRequestAutoApprovalSetting}
              reasonData={creditRequestReasons}
              onChange={data => {
                // Update this field value
                setFormValues(current => {
                  const parsedCurrent = current || {};

                  return {
                    ...parsedCurrent,
                    credit_request_auto_approval: data
                  };
                });
              }}
            />
          </>
        )
      }
    };
  // Only update if the given dependencies update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canAmend,
    inheritedSettings,
    formValues
  ]);

  /**
   * Handle submit settings
   */
  const handleSubmitSettings: React.FormEventHandler<HTMLFormElement> = React.useCallback(() => {
    _.each(formValues, (settingValue, settingName) => {
      const overrideSettingValue = overrideSettings?.[ settingName ];

      // Only save the settings within this page
      if (settings?.[ settingName ]) {
      // If there is no overrideSettingValue for this, create it. Otherwise update it
        if (!overrideSettingValue) {
          createSettingReq({
            entityId: dropId,
            name: settingName,
            scope: "branch",
            value: settingValue
          } as CreateSettingParams);
        } else {
          updateSettingReq({
            ...overrideSettingValue,
            value: settingValue
          });
        }
      }
    });
  }, [
    createSettingReq,
    dropId,
    formValues,
    overrideSettings,
    settings,
    updateSettingReq
  ]);

  // On mount
  React.useEffect(() => {
    getInheritedSettingsReq({ dropId });
  }, [ dropId, getInheritedSettingsReq ]);

  // On getInheritedSettingsRes change
  React.useEffect(() => {
    // On error
    if (getInheritedSettingsRes.error) {
      dispatch(setGlobalError(getInheritedSettingsRes.error));
    }

    // On success
    if (getInheritedSettingsRes.data) {
      const inheritedToSet: OrderedInheritedSettings<IInheritedSetting> = {};
      const overridesToSet: OrderedInheritedSettings<IInheritedSetting> = {};
      const formValues: IFormValues = {};

      _.each(getInheritedSettingsRes.data, settings => {
        if (settings) {
          const globalSetting = settings.find(setting => setting.scope === "global");
          const customerSetting = settings.find(setting => setting.scope === "customer");
          const branchSetting = settings.find(setting => setting.scope === "branch");

          if (customerSetting) {
            inheritedToSet[ customerSetting.name ] = customerSetting;
          } else if (globalSetting) {
            inheritedToSet[ globalSetting.name ] = globalSetting;
          }

          if (branchSetting) {
            overridesToSet[ branchSetting.name ] = branchSetting;
            // Set the form values object
            formValues[ branchSetting.name ] = branchSetting.value;
          }
        }
      });
      setInheritedSettings(inheritedToSet);
      setOverrideSettings(overridesToSet);
      setFormValues(formValues);
    }
  }, [
    dispatch,
    getInheritedSettingsRes.error,
    getInheritedSettingsRes.data
  ]);

  // On createSettingRes change
  React.useEffect(() => {
    // On failure
    if (createSettingRes.error) {
      toast.error(createSettingRes.error.message);
    }

    // On success
    if (createSettingRes.data) {
      toast.success("Settings saved!");
    }
  }, [ createSettingRes ]);

  // On updateSettingRes change
  React.useEffect(() => {
    // On failure
    if (updateSettingRes.error) {
      toast.error(updateSettingRes.error.message);
    }

    // On success
    if (updateSettingRes.data) {
      toast.success("Settings saved!");
    }
  }, [ updateSettingRes ]);

  return (
    <Layout
      {...props}
      title={drop ? `${drop.name} - Credit request settings` : ""}
    >
      <MainContentWrapper fill>
        {getInheritedSettingsRes.pending || isLoadingDrop ? (
          <LoadingSpinner type="circular" />
        ) : (
          <Grid.Bounds direction="vertical">
            <form onSubmit={handleSubmit(handleSubmitSettings)}>
              {_.map(settings, (setting, i) => {
                if (setting.hidden) {
                  return null;
                }

                return (
                  <InheritedSettingsWrapper key={`setting-${i}`}>
                    <InheritedSettingsField
                      id={`${setting.name}-${i}`}
                      key={setting.name}
                    >
                      {setting.Description}

                      {setting.SettingElement(setting)}
                    </InheritedSettingsField>
                  </InheritedSettingsWrapper>
                );
              })}

              {canAmend && (
                <Button
                  loading={isLoading}
                  disabled={isLoading}
                  type="submit"
                >
                  Save
                </Button>
              )}
            </form>
          </Grid.Bounds>
        )}
      </MainContentWrapper>

      {/* Formik form, needs ironing out */}

      {/* <InfoBarWrapper>
        <DropInfoSidebar
          terminology={terminology}
          isLoading={isLoadingDrop}
          data={drop}
          primaryAction={(
            <ButtonLink $buttonType="secondary" to={`/admin/customers/${customerId}/drops`}>
              Cancel
            </ButtonLink>
          )}
          secondaryAction={(
            <Button loading={submitting} onClick={submitForm} disabled={!canAmend}>
              Save
            </Button>
          )}
        />
      </InfoBarWrapper> */}
    </Layout>
  );
};

export default DropCreditRequestSettingsPage;
